import { useCallback, useEffect } from 'react';
import { CheckoutSection } from '@/common.styled';
import { Heading } from '@/components/Heading';
import {
  NotificationMessage,
  NotificationsQueue,
} from '@/components/Notifications';
import { RadioButtonGroup } from '@/components/RadioButtonGroup';
import { DeliveryOption, ValidationSectionKey } from '@/constants';
import { useShipping } from '@/contexts/shippingContext';
import { useValidation } from '@/contexts/validationContext';
import { useSetDeliveryMethod } from '@/hooks/useSetDeliveryMethod';
import type { StandardApiResponse } from '@/types';
import * as Styled from './DeliveryMethod.styled';

const headingId = 'deliveryMethod';

const onSuccess = (data: StandardApiResponse) => {
  if (!data.isSuccess) {
    NotificationsQueue.add(
      {
        notification: (
          <NotificationMessage $isError={true}>
            {data.messages?.[0]}
          </NotificationMessage>
        ),
      },
      { timeout: 5000 },
    );
  }
};

export const DeliveryMethod = () => {
  const {
    orderPayload: { deliveryOption, shippingAddress },
    shippingMethod,
    shippingOptions,
  } = useShipping();

  const { clearValidationError, errorMessages } = useValidation();

  const shippingMethodError =
    errorMessages[ValidationSectionKey.ShippingMethod];

  const { setShippingMethod } = useSetDeliveryMethod();

  const firstShippingOptionValue = shippingOptions?.[0].value ?? '';

  useEffect(() => {
    if (firstShippingOptionValue) {
      setShippingMethod({ shippingMethodName: firstShippingOptionValue });
    }
  }, [firstShippingOptionValue, setShippingMethod]);

  const handleChange = useCallback(
    (shippingMethodName: string) => {
      setShippingMethod({ shippingMethodName }, { onSuccess });
      clearValidationError(ValidationSectionKey.ShippingMethod);
    },
    [clearValidationError, setShippingMethod],
  );

  const showMessage =
    deliveryOption === DeliveryOption.Ship && !shippingAddress.countryName;

  return (
    <CheckoutSection
      className={shippingMethodError ? 'has-validation-error' : undefined}
    >
      <Heading id={headingId} level={2}>
        Delivery method
      </Heading>
      {showMessage ? (
        <Styled.Message>
          Enter your delivery address to view available delivery methods.
        </Styled.Message>
      ) : (
        <RadioButtonGroup
          aria-labelledby={headingId}
          error={shippingMethodError}
          fields={shippingOptions}
          name={ValidationSectionKey.ShippingMethod}
          onChange={handleChange}
          value={shippingMethod ?? ''}
        />
      )}
    </CheckoutSection>
  );
};
